/* ============================================================
   HEADER GEDRAG
   Web components, geen framework. De statusklassen zijn exact
   dezelfde als in het origineel, zodat header.css 1:1 werkt:

     header.is-scroll-down            header schuift weg
     header.header--transition        header terug naar wit/zwart
     .header__menu-toggle.is-open     hamburger -> kruis
     .header__section--bottom.is-open lade open
     .menu__submenu.is-open           submenu open in de lade
     .menu__item.is-submenu-open      mega menu open (horizontale balk)
     body.menu-is-open                scroll-lock

   Let op: dit bestand wordt met een gewoon <script defer> geladen, niet als
   module. Een import of export erin is een syntaxfout en dan draait het hele
   bestand niet, dus bestaat t-menu niet en doet de hamburger niets. De twee
   media queries staan daarom op window in plaats van in een export.
   ============================================================ */

window.MQ_DESKTOP = window.matchMedia('(min-width: 1024px)');
window.REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');
const MQ_DESKTOP = window.MQ_DESKTOP;

/* ---------- t-menu ---------------------------------------- */
class TMenu extends HTMLElement {
  connectedCallback() {
    this.header = this.closest('header');
    this.toggle = this.header?.querySelector('[data-menu-toggle]');
    this.items = [...this.querySelectorAll('.menu__item')].filter((i) => i.querySelector('.menu__submenu'));

    this.toggle?.addEventListener('click', () => this.toggleDrawer());

    this.items.forEach((item) => {
      const arrow = item.querySelector('.menu__level-1-arrow');
      const back = item.querySelector('[data-submenu-back]');
      const panel = item.querySelector('.menu__submenu');

      // in de lade: openen via de pijl of het item zelf, sluiten via terug
      arrow?.addEventListener('click', (e) => {
        if (!this.ladeModus() && MQ_DESKTOP.matches) return;
        e.preventDefault();
        e.stopPropagation();
        this.openSub(item, panel, arrow);
      });
      item.addEventListener('click', (e) => {
        if (!this.ladeModus() && MQ_DESKTOP.matches) return;
        if (e.target.closest('a[href]') || e.target.closest('.menu__submenu')) return;
        if (!panel) return;
        e.preventDefault();
        this.openSub(item, panel, arrow);
      });
      back?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.closeSub(item, panel, arrow);
      });

      // horizontale balk: openen op hover en op focus binnen het item
      item.addEventListener('mouseenter', () => this.balkModus() && this.openDesktop(item));
      item.addEventListener('mouseleave', () => this.balkModus() && this.closeDesktop(item));
      item.addEventListener('focusin', () => this.balkModus() && this.openDesktop(item));
      item.addEventListener('focusout', (e) => {
        if (this.balkModus() && !item.contains(e.relatedTarget)) this.closeDesktop(item);
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      this.items.forEach((i) => this.closeDesktop(i));
      if (this.classList.contains('is-open')) this.toggleDrawer();
    });

    MQ_DESKTOP.addEventListener('change', () => this.reset());
  }

  /* Staat de hamburger in beeld, dan is de navigatie een lade. Dat is de enige
     betrouwbare check: of de lade gebruikt wordt is een sectie-instelling die
     alleen in de CSS landt, en op desktop kan het allebei. */
  ladeModus() {
    return !!this.toggle && getComputedStyle(this.toggle).display !== 'none';
  }

  balkModus() {
    return MQ_DESKTOP.matches && !this.ladeModus();
  }

  toggleDrawer() {
    const open = !this.classList.contains('is-open');
    this.classList.toggle('is-open', open);
    this.toggle?.classList.toggle('is-open', open);
    this.toggle?.setAttribute('aria-expanded', String(open));
    document.body.classList.toggle('menu-is-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    if (!open) this.items.forEach((i) => this.closeSub(i));
  }

  openSub(item, panel, arrow) {
    panel = panel || item.querySelector('.menu__submenu');
    arrow = arrow || item.querySelector('.menu__level-1-arrow');
    panel?.classList.add('is-open');
    arrow?.setAttribute('aria-expanded', 'true');
    panel?.querySelector('[data-submenu-back]')?.focus();
  }

  closeSub(item, panel, arrow) {
    panel = panel || item.querySelector('.menu__submenu');
    arrow = arrow || item.querySelector('.menu__level-1-arrow');
    panel?.classList.remove('is-open');
    arrow?.setAttribute('aria-expanded', 'false');
  }

  openDesktop(item) {
    this.items.forEach((i) => i !== item && i.classList.remove('is-submenu-open'));
    item.classList.add('is-submenu-open');
    item.querySelector('.menu__level-1-arrow')?.setAttribute('aria-expanded', 'true');
  }

  closeDesktop(item) {
    item.classList.remove('is-submenu-open');
    item.querySelector('.menu__level-1-arrow')?.setAttribute('aria-expanded', 'false');
  }

  reset() {
    this.classList.remove('is-open');
    this.toggle?.classList.remove('is-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('menu-is-open');
    document.body.style.overflow = '';
    this.items.forEach((i) => { this.closeSub(i); this.closeDesktop(i); });
  }
}
if (!customElements.get('t-menu')) customElements.define('t-menu', TMenu);

/* ---------- t-hover-header --------------------------------
   Twee losse dingen, allebei aan de scrollpositie:

     is-scroll-down      de header schuift omhoog uit beeld bij naar beneden
                         scrollen en komt terug zodra je omhoog scrolt. Staat
                         data-hide-on-scroll op false, dan gebeurt dit niet en
                         blijft de balk gewoon staan.
     header--transition  de header wordt weer wit met donkere tekst zodra je
                         voorbij de drempel bent. Dit blijft altijd aan, ook
                         als de header niet wegschuift, anders staat witte
                         tekst van een transparante hero straks op een witte
                         pagina.                                              */
class THoverHeader extends HTMLElement {
  connectedCallback() {
    this.header = this.closest('header');
    this.threshold = Number(this.dataset.scrollThreshold || 80);
    this.verbergen = this.dataset.hideOnScroll !== 'false';
    this.last = window.scrollY;
    this.ticking = false;

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => this.update());
    };
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.update();
  }

  disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }

  update() {
    const y = window.scrollY;
    const down = y > this.last && y > this.threshold;
    // menu open? dan blijft de header staan
    const locked = document.body.classList.contains('menu-is-open') ||
                   this.header?.querySelector('.menu__item.is-submenu-open');

    this.header?.classList.toggle('is-scroll-down', this.verbergen && down && !locked);
    this.header?.classList.toggle('header--transition', y > this.threshold);
    this.last = y;
    this.ticking = false;
  }
}
if (!customElements.get('t-hover-header')) customElements.define('t-hover-header', THoverHeader);

/* ---------- mobile-search-bar ------------------------------ */
class MobileSearchBar extends HTMLElement {
  connectedCallback() {
    const open = () => document.dispatchEvent(new CustomEvent('search:open'));
    this.addEventListener('click', open);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });
  }
}
if (!customElements.get('mobile-search-bar')) customElements.define('mobile-search-bar', MobileSearchBar);

/* ---------- slide-push-mobile ------------------------------
   Horizontale scroll-snap rij met bolletjes eronder.            */
class SlidePushMobile extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.slide-push-menu-mobile');
    this.bullets = [...this.querySelectorAll('.slide-push-menu-mobile__bullet')];
    if (!this.track || !this.bullets.length) return;

    this.track.addEventListener('scroll', () => {
      if (this.raf) return;
      this.raf = requestAnimationFrame(() => {
        const i = Math.round(this.track.scrollLeft / (this.track.scrollWidth / this.bullets.length));
        this.bullets.forEach((b, n) =>
          b.classList.toggle('slide-push-menu-mobile__bullet--active', n === Math.min(i, this.bullets.length - 1)));
        this.raf = null;
      });
    }, { passive: true });
  }
}
if (!customElements.get('slide-push-mobile')) customElements.define('slide-push-mobile', SlidePushMobile);

/* ---------- localization-trigger ---------------------------
   Staat niet meer in de header, maar kan elders in het thema nog
   voorkomen (footer, menu). Daarom blijft hij geregistreerd.     */
class LocalizationTrigger extends HTMLElement {
  connectedCallback() {
    const open = () => document.dispatchEvent(new CustomEvent('localization:open'));
    this.addEventListener('click', open);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });
  }
}
if (!customElements.get('localization-trigger')) customElements.define('localization-trigger', LocalizationTrigger);
